import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'sharedUi/Button';
import { actionMastersService } from '../../services/action-masters.service';
import { referencableIn } from '../../hooks/useProjectScope';
import type { ActionAttachment } from '../../services/recipient-masters.service';

/**
 * The actions offered to whoever a recipient resolves to.
 *
 * `notificationMasterId` is nullable on purpose: left empty the attachment is
 * global to the recipient, set it and the action behaves differently on that one
 * notification — which is what lets APPROVE mean "ACTIONED" everywhere and
 * "COMPLETED" on a single case, without copying the action.
 */
interface ActionAttachmentEditorProps {
  value: ActionAttachment[];
  onChange: (actions: ActionAttachment[]) => void;
  /** Notifications this recipient is used by, for the scoped-override picker. */
  notificationOptions?: Array<{ value: string; label: string }>;
  /** The project this belongs to — only its own and shared actions may be
   *  attached, which is what the API accepts. */
  projectId?: string | null;
  testId?: string;
}

const ActionAttachmentEditor = ({
  value, onChange, notificationOptions = [], projectId, testId = 'ActionAttachmentEditor',
}: ActionAttachmentEditorProps) => {
  const { data: actionsPage } = useQuery({
    queryKey: ['actionMasters', 'attachable'],
    queryFn: () => actionMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });

  // A deactivated action is refused by the API, so it is not offered — nor is
  // another project's, which the save would reject as out of scope.
  const actions = (actionsPage?.data || [])
    .filter((a) => a.isActive !== false)
    .filter(referencableIn(projectId));
  const actionOptions = actions.map((a) => ({ value: a._id, label: a.actionCode }));

  const update = (index: number, next: ActionAttachment) =>
    onChange(value.map((a, i) => (i === index ? next : a)));

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Actions</label>
        <span className="block text-xs text-gray-400">
          What this audience may do about the notification. Any of them is available
          until the recipient has finished with it; taking one records that they responded.
        </span>
      </div>

      {value.length === 0 && (
        <p className="text-xs text-gray-400">None — recipients can read the notification but not respond.</p>
      )}

      {value.map((attachment, index) => (
        <div key={index} className="border border-line rounded p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown
              value={attachment.actionMasterId}
              options={actionOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Action"
              className="w-48"
              data-testid={`${testId}.Action.${index}`}
              onChange={(e) => update(index, { ...attachment, actionMasterId: e.value })}
            />
            <div className="flex items-center gap-1.5">
              <Checkbox
                inputId={`${testId}-default-${index}`}
                checked={!!attachment.isDefault}
                onChange={(e) => update(index, { ...attachment, isDefault: !!e.checked })}
              />
              <label htmlFor={`${testId}-default-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
                Default
              </label>
            </div>
            <button aria-label="Remove action"
              type="button"
              title="Remove action"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ml-auto"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <i className="pi pi-trash text-red-600 dark:text-red-400" />
            </button>
          </div>

          {notificationOptions.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-ink-muted w-28 shrink-0">Scope</span>
              <Dropdown
                value={attachment.notificationMasterId || ''}
                options={[{ value: '', label: 'Every notification (global)' }, ...notificationOptions]}
                optionLabel="label"
                optionValue="value"
                className="flex-1"
                data-testid={`${testId}.Scope.${index}`}
                onChange={(e) => update(index, { ...attachment, notificationMasterId: e.value || null })}
              />
            </div>
          )}
        </div>
      ))}

      <div>
        <Button type="button" label="Add action" icon="pi pi-plus" size="small" severity="secondary" outlined
          onClick={() => onChange([...value, { actionMasterId: '', isDefault: false }])}
          data-testid={`${testId}.Add`} />
      </div>
    </div>
  );
};

export default ActionAttachmentEditor;
